import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth';
import { PillButton, PillInput, PillSelect } from '@/components/FormField';
import { IconPencil } from '@/components/Icon';
import { updateUserFields, type UserDoc } from '@/services/firestore/users';

const APP_VERSION = `${__APP_VERSION__} (${__APP_BUILD__})`;

const MD_TYPES = [
  { value: 'cfpc', label: 'CFPC' },
  { value: 'frcp', label: 'FRCP' },
];

const BILLING_TYPES = [
  { value: 'mixte', label: 'Mixte' },
  { value: 'acte', label: 'Acte' },
];

const MD_TYPE_LABEL: Record<string, string> = {
  cfpc: 'CFPC',
  frcp: 'FRCP',
};

const BILLING_TYPE_LABEL: Record<string, string> = {
  mixte: 'Mixte',
  acte: 'Acte',
};

const UPPERCASE_FIELDS = new Set<keyof UserDoc>(['mainPlace', 'secondaryPlace']);

type Editable = Pick<
  UserDoc,
  | 'firstName'
  | 'lastName'
  | 'mdType'
  | 'billingType'
  | 'mainPlace'
  | 'secondaryPlace'
  | 'billingAgency'
  | 'billingEmail'
>;

function Row({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 text-sm">
      <span className="text-white/70 shrink-0">{label}</span>
      <span className="font-semibold text-right truncate">{value || '—'}</span>
    </div>
  );
}

function Card({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="bg-brand-dark/40 rounded-2xl p-4 shadow-card flex flex-col gap-3">
      <p className="text-xs text-white/60 uppercase tracking-wide">{title}</p>
      {children}
    </section>
  );
}

export function ProfilePage() {
  const profile = useAuthStore((s) => s.profile);
  const signOut = useAuthStore((s) => s.signOut);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<Editable | null>(null);

  if (!profile) return null;

  function startEdit() {
    if (!profile) return;
    setDraft({
      firstName: profile.firstName,
      lastName: profile.lastName,
      mdType: profile.mdType,
      billingType: profile.billingType,
      mainPlace: profile.mainPlace,
      secondaryPlace: profile.secondaryPlace,
      billingAgency: profile.billingAgency,
      billingEmail: profile.billingEmail,
    });
    setError(null);
    setEditing(true);
  }

  function cancelEdit() {
    setDraft(null);
    setError(null);
    setEditing(false);
  }

  function updateDraft<K extends keyof Editable>(key: K, value: Editable[K]) {
    setDraft((d) => (d ? { ...d, [key]: value } : d));
  }

  async function saveDraft() {
    if (!profile || !draft) return;
    const changes: Partial<UserDoc> = {};
    (Object.keys(draft) as Array<keyof Editable>).forEach((k) => {
      const raw = draft[k];
      const value =
        typeof raw === 'string' && UPPERCASE_FIELDS.has(k)
          ? raw.toUpperCase()
          : raw;
      if (value !== profile[k]) (changes as Record<string, unknown>)[k] = value;
    });
    if (Object.keys(changes).length === 0) {
      cancelEdit();
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await updateUserFields(profile.uid, changes);
      useAuthStore.setState({ profile: { ...profile, ...changes } });
      setDraft(null);
      setEditing(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  const fullName = [profile.firstName, profile.lastName].filter(Boolean).join(' ');

  return (
    <div className="p-6 flex flex-col gap-4 pb-24">
      <header className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Profil</h1>
        {!editing && (
          <button
            type="button"
            onClick={startEdit}
            className="inline-flex items-center gap-1.5 text-sm text-brand-orange font-semibold"
          >
            <IconPencil size={16} />
            Modifier
          </button>
        )}
      </header>

      {editing && draft ? (
        <>
          <Card title="Identité">
            <div className="flex gap-2">
              <PillInput
                placeholder="Prénom"
                value={draft.firstName}
                onChange={(e) => updateDraft('firstName', e.target.value)}
              />
              <PillInput
                placeholder="Nom"
                value={draft.lastName}
                onChange={(e) => updateDraft('lastName', e.target.value)}
              />
            </div>
            <Row label="Courriel" value={profile.email} />
          </Card>

          <Card title="Pratique">
            <div className="flex gap-2">
              <PillSelect
                options={MD_TYPES}
                value={draft.mdType || 'cfpc'}
                onChange={(e) => updateDraft('mdType', e.target.value)}
              />
              <PillSelect
                options={BILLING_TYPES}
                value={draft.billingType || 'mixte'}
                onChange={(e) => updateDraft('billingType', e.target.value)}
              />
            </div>
            <PillInput
              placeholder="Lieu principal"
              value={draft.mainPlace}
              onChange={(e) => updateDraft('mainPlace', e.target.value)}
            />
            <PillInput
              placeholder="Lieu secondaire"
              value={draft.secondaryPlace}
              onChange={(e) => updateDraft('secondaryPlace', e.target.value)}
            />
          </Card>

          <Card title="Facturation">
            <PillInput
              placeholder="Agence"
              value={draft.billingAgency}
              onChange={(e) => updateDraft('billingAgency', e.target.value)}
            />
            <PillInput
              type="email"
              placeholder="Courriel agence"
              value={draft.billingEmail}
              onChange={(e) => updateDraft('billingEmail', e.target.value)}
            />
          </Card>

          {error && <p className="text-sm text-red-300 px-1">{error}</p>}

          <div className="flex gap-2">
            <button
              type="button"
              onClick={cancelEdit}
              disabled={saving}
              className="flex-1 rounded-pill bg-white/10 text-white font-semibold py-3 disabled:opacity-50"
            >
              Annuler
            </button>
            <PillButton
              onClick={() => void saveDraft()}
              disabled={saving}
              className="flex-1"
            >
              {saving ? 'Enregistrement…' : 'Enregistrer'}
            </PillButton>
          </div>
        </>
      ) : (
        <>
          <Card title="Identité">
            <Row label="Nom" value={fullName} />
            <Row label="Courriel" value={profile.email} />
          </Card>
          <Card title="Pratique">
            <Row label="Type" value={MD_TYPE_LABEL[profile.mdType] ?? profile.mdType} />
            <Row
              label="Rémunération"
              value={BILLING_TYPE_LABEL[profile.billingType] ?? profile.billingType}
            />
            <Row label="Lieu principal" value={profile.mainPlace} />
            <Row label="Lieu secondaire" value={profile.secondaryPlace} />
          </Card>
          <Card title="Facturation">
            <Row label="Agence" value={profile.billingAgency} />
            <Row label="Courriel agence" value={profile.billingEmail} />
          </Card>
        </>
      )}

      <section className="bg-brand-dark/40 rounded-2xl p-4 shadow-card flex flex-col gap-2">
        <Link to="/favorites" className="text-sm text-brand-orange underline">
          Gérer les favoris
        </Link>
        <p className="text-xs text-white/50 mt-2">Version {APP_VERSION}</p>
      </section>

      {!editing && (
        <PillButton onClick={() => void signOut()}>Déconnexion</PillButton>
      )}
    </div>
  );
}
